<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';

import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';

interface Props {
  text: string;
}

const props = defineProps<Props>();

const body = ref<ComponentPublicInstance | null>(null);
const expanded = ref(false);
const overflows = ref(false);

// The toggle is only worth showing when the clamp actually hides something, so the collapsed
// paragraph is measured rather than guessed from a character count.
const measure = () => {
  const paragraph = body.value?.$el as HTMLElement | undefined;
  if (paragraph && !expanded.value) {
    overflows.value = paragraph.scrollHeight > paragraph.clientHeight;
  }
};

onMounted(() => {
  measure();
  window.addEventListener('resize', measure);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', measure);
});

watch(() => props.text, () => nextTick(measure));
</script>

<template>
  <div
    v-if="text"
    class="flex flex-col gap-4 rounded-3xl bg-background p-4"
    data-testid="video-description"
  >
    <Text
      id="video-description-body"
      ref="body"
      size="sm"
      class="whitespace-pre-line"
      :class="{ 'line-clamp-3': !expanded }"
    >
      {{ text }}
    </Text>
    <Button
      v-if="overflows"
      variant="link"
      size="sm"
      type="button"
      class="w-fit px-0 underline"
      :aria-expanded="expanded"
      aria-controls="video-description-body"
      data-testid="video-description-toggle"
      @click="expanded = !expanded"
    >
      <Text as="span" size="sm">
        {{ expanded ? $t('unified.videos.showLess') : $t('unified.videos.showMore') }}
      </Text>
      <Icon :name="expanded ? ICONS.arrowUp : ICONS.arrowDown" />
    </Button>
  </div>
</template>
